// src/api/endpoints.js
// -----------------------------------------------------------------------------
// Central place that lists every REST endpoint path used by this frontend.
// Keeping paths here (instead of hard-coding strings inside each service file)
// means if a route changes on the C# backend, you only update it in ONE spot.
//
// NOTE: These paths are named after the C# backend's expected controllers,
// based on the AFix table/entity names (see Bug_Tracking_System_Schema.xlsx
// and AFix_Functional_Requirements.pdf). If your actual ASP.NET Core route
// names differ, just edit the strings below — nothing else needs to change.
// -----------------------------------------------------------------------------

const ENDPOINTS = {
  // ---------- Auth (Requirement #2: JWT login) ----------
  AUTH_LOGIN: '/auth/login',
  AUTH_REGISTER: '/auth/register',
  AUTH_REFRESH: '/auth/refresh-token',
  AUTH_FORGOT_PASSWORD: '/auth/forgot-password',
  AUTH_CHANGE_PASSWORD: '/auth/change-password',
  AUTH_RESET_PASSWORD: '/auth/reset-password',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_ME: '/Auth/me', // GET / PUT the logged-in user's own profile

  // ---------- Role_TBL (Requirement #3) ----------
  ROLES: '/roles',
  ROLE_BY_ID: (roleID) => `/roles/${roleID}`,

  // ---------- User_TBL (Requirement #4, #24) ----------
  USERS: '/Auth/get-all',
  USER_BY_ID: (userID) => `/Auth/get-by-id/${userID}`,
  USER_DEACTIVATE: (userID) => `/Auth/deactivate/${userID}`,
  USER_REACTIVATE: (userID) => `/Auth/reactivate/${userID}`,
  USER_UNLOCK: (userID) => `/Auth/unlock/${userID}`,
  USER_DELETE: (userID) => `/Auth/delete/${userID}`,
  USER_UPDATE: '/Auth/update',
  USER_UPDATE_ROLE: '/Auth/update-role',

  // ---------- Client_Organization_TBL ----------
  CLIENT_ORGS: '/clientorganizations',
  CLIENT_ORG_BY_ID: (clientOrgID) => `/clientorganizations/${clientOrgID}`,

  // ---------- Project_TBL (Requirement #9) ----------
  PROJECTS: '/projects',
  PROJECT_BY_ID: (projectID) => `/projects/${projectID}`,

  // ---------- Project_Phase_TBL (Requirement #7, #8, #10) ----------
  PROJECT_PHASES: (projectID) => `/projects/${projectID}/phases`,
  PROJECT_PHASE_BY_ID: (projectID, phaseID) => `/projects/${projectID}/phases/${phaseID}`,

  // ---------- Project_Team_TBL / Project_Team_Member_TBL (Requirement #11) ----------
  PROJECT_TEAMS: (projectID) => `/projects/${projectID}/teams`,
  PROJECT_TEAM_MEMBERS: (projectID, teamID) => `/projects/${projectID}/teams/${teamID}/members`,

  // ---------- Project_Environment_TBL (Requirement #26, #27) ----------
  PROJECT_ENVIRONMENTS: (projectID) => `/projects/${projectID}/environments`,
  PROJECT_ENVIRONMENTS_BASE: '/ProjectEnvironment',
  PROJECT_ENVIRONMENT_BY_ID: (id) => `/ProjectEnvironment/${id}`,

  // ---------- Ticket_Type_TBL / Ticket_Sub_Type_TBL (Requirement #6, #25 - global, Admin only) ----------
  TICKET_TYPES: '/tickettypes',
  TICKET_TYPE_BY_ID: (ticketTypeID) => `/tickettypes/${ticketTypeID}`,
  TICKET_SUB_TYPES: (ticketTypeID) => `/tickettypes/${ticketTypeID}/subtypes`,

  // ---------- Ticket_Status_TBL / Ticket_Status_Transition_TBL (Requirement #16) ----------
  PROJECT_STATUSES: (projectID) => `/projects/${projectID}/statuses`,
  PROJECT_STATUS_TRANSITIONS: (projectID) => `/projects/${projectID}/status-transitions`,

  // ---------- Ticket_Severity_TBL (Requirement #17) ----------
  PROJECT_SEVERITIES: (projectID) => `/projects/${projectID}/severities`,

  // ---------- Ticket_Reopen_Reason_TBL (Requirement #28) ----------
  PROJECT_REOPEN_REASONS: (projectID) => `/projects/${projectID}/reopen-reasons`,

  // ---------- Client_Question_TBL (Requirement #18) ----------
  PROJECT_CLIENT_QUESTIONS: (projectID) => `/projects/${projectID}/client-questions`,

  // ---------- Ticket_TBL (Requirement #1, #12, #13, #14, #20, #21) ----------
  TICKETS: '/tickets',
  TICKET_BY_ID: (ticketID) => `/tickets/${ticketID}`,
  TICKET_HISTORY: (ticketID) => `/tickets/${ticketID}/history`,          // Ticket_Status_History_TBL
  TICKET_ASSIGNMENTS: (ticketID) => `/tickets/${ticketID}/assignments`,  // Ticket_Assignment_TBL
  TICKET_ASSIGNMENTS_BASE: '/TicketAssignment',
  TICKET_ASSIGNMENT_BY_ID: (id) => `/TicketAssignment/${id}`,
  TICKET_COMMENTS: (ticketID) => `/tickets/${ticketID}/comments`,        // Ticket_Comment_TBL (#15, #19)
  TICKET_ATTACHMENTS: (ticketID) => `/tickets/${ticketID}/attachments`,  // Ticket_Attachment_TBL
  TICKET_LINKS: (ticketID) => `/tickets/${ticketID}/links`,              // Ticket_Link_TBL
  TICKET_CLIENT_ANSWERS: (ticketID) => `/tickets/${ticketID}/answers`,   // Ticket_Client_Answer_TBL
  TICKET_MANDATORY_FIELDS_BASE: '/TicketMandatoryField',
  TICKET_MANDATORY_FIELD_BY_ID: (id) => `/TicketMandatoryField/${id}`,

  // ---------- Notification_TBL (Requirement #22) ----------
  NOTIFICATIONS: '/Notification',
  NOTIFICATION_BY_ID: (notificationID) => `/Notification/${notificationID}`,
  NOTIFICATION_BY_USER: (userID) => `/Notification/user/${userID}`,
  NOTIFICATION_UNREAD_BY_USER: (userID) => `/Notification/user/${userID}/unread`,
  NOTIFICATION_MARK_READ: (notificationID) => `/Notification/${notificationID}/read`,
  NOTIFICATION_MARK_ALL_READ: (userID) => `/Notification/user/${userID}/read-all`,

  // ---------- Dashboard / Reports (Requirement #23) ----------
  DASHBOARD_SUMMARY: '/Report/summary',
  REPORTS_LONG_PENDING: '/Report/long-pending',
  REPORTS_EXPORT_EXCEL: '/Report/export',
  REPORTS_SEARCH: '/Report/search',

  // ---------- Audit_Log_TBL ----------
  AUDIT_LOGS: '/AuditLog', // Admin only, read-only

  // ---------- Permission_TBL (Permission Override Screen) ----------
  PERMISSIONS: '/Permission',
  PERMISSION_BY_ID: (permissionID) => `/Permission/${permissionID}`,
};

export default ENDPOINTS;
